import { RollbackDelta } from 'src/features/string-group-detail/utils/rollbackDelta';
import {
  SubmissionType,
  SubmitStringGroupTranslationMutationVariables,
  useSubmitStringGroupTranslationMutation,
} from 'src/generated/graphql';
import { normalizeLocaleIdentifier } from 'src/common/utils/locale';
import {
  DeniedNamespace,
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import {
  ChangeControlRefusal,
  extractSubmitError,
  toChangeControlRefusal,
  toMcmIdVariable,
} from 'src/features/string-group-detail/hooks/useSubmitTranslation';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** For a rollback failure the service did not describe. A message it DID send is upstream content
 * and surfaced verbatim. */
export const ROLLBACK_FAILED_FALLBACK_KEY = 'sgd-rollback-error-generic';

/**
 * A failed submission, carrying the classification the caller needs to frame it:
 * a refusal is terminal and shows the service's wording alone, a fault is worth
 * retrying.
 */
export interface SubmitFailure {
  /** The service's own message wherever it sent one. */
  readonly message: string;
  /** True for an authorization refusal. */
  readonly accessDenied: boolean;
  /**
   * The namespace a refusal named, which the caller turns into a Bindle link.
   * Kept out of `message` because that string is rendered verbatim. Absent for
   * every non-refusal and for a refusal the service sent no detail with.
   */
  readonly deniedNamespace?: DeniedNamespace;
  /**
   * Present only when the gate refused the submission on change-control grounds.
   * The caller shows this in `ChangeControlBlockedModal` rather than the generic
   * error alert: it is the one failure the operator must act on before retrying
   * (cite a different MCM, or wait for the freeze to lift), so it must be read
   * rather than noticed. Classified by the same `toChangeControlRefusal` the
   * submit path uses, so a rollback and a submit never disagree about what
   * counts as a refusal.
   */
  readonly changeControlBlocked?: ChangeControlRefusal;
}

/**
 * Everything one rollback submission needs. An options object rather than a
 * positional list because most of these are `string` or `number`: a transposed
 * pair of positional arguments type-checks cleanly and submits the wrong
 * namespace or version, whereas a misnamed option does not compile.
 */
export interface SubmitGroupForTranslationOptions {
  /** The previewed delta. Submitting is a no-op without one — Confirm is disabled until it exists. */
  readonly delta: RollbackDelta | null;
  /** Passed in rather than read from a hook: this is a plain async function, and the caller
   * already holds `t`. */
  readonly t: TranslateFn;
  readonly namespaceId: string;
  readonly stringGroupId: string;
  readonly cxIds: string[];
  readonly sourceLocale: string;
  readonly cxFamilyId: string;
  /** The version the delta was computed against; sent as `baseVersion`. */
  readonly currentVersion: number;
  /** The rollback target, used only as the fallback when the response omits a version. */
  readonly version: number;
  /**
   * The recipient for the completion notification, already validated and
   * trimmed by the modal. Sent as the `notificationEmail` mutation variable the
   * same way a routine submit sends it (`useSubmitTranslation`); a blank
   * collapses to an absent variable so an empty string never reaches the
   * resolver.
   */
  readonly notificationEmail: string;
  /**
   * The change-control override the operator cited, or `undefined` when none was
   * collected — an exempt rollback, or a normal day. Blank collapses to absent
   * (see `toMcmIdVariable`): the backend refuses a blank with a 400, so an
   * operator who typed nothing must submit as "no override cited" rather than as
   * "an invalid override".
   */
  readonly mcmId?: string;
  /** The mutate function from `useSubmitStringGroupTranslationMutation`. */
  readonly submitMutation: ReturnType<
    typeof useSubmitStringGroupTranslationMutation
  >[0];
  readonly setSubmitting: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  readonly setError: (failure: SubmitFailure | null) => void;
  readonly setNewVersion: (
    value: ((prevState: number | null) => number | null) | number | null,
  ) => void;
  readonly setShowSuccess: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
}

export async function submitGroupForTranslation({
  delta,
  t,
  namespaceId,
  stringGroupId,
  cxIds,
  sourceLocale,
  cxFamilyId,
  currentVersion,
  version,
  notificationEmail,
  mcmId,
  submitMutation,
  setSubmitting,
  setError,
  setNewVersion,
  setShowSuccess,
}: SubmitGroupForTranslationOptions): Promise<void> {
  /* istanbul ignore next -- defensive guard: Confirm is disabled until a
     delta exists (`!previewReady` covers `!delta`), so this is unreachable
     through the UI. */
  if (!delta) {
    return;
  }
  setSubmitting(true);
  setError(null);

  try {
    // Submit only non-empty arrays.
    const variables: SubmitStringGroupTranslationMutationVariables = {
      namespaceId,
      stringGroupId,
      cxIds,
      // Frozen group-wide, so a rollback sends the group's current values — the
      // same ones a routine submit sends (useSubmitTranslation). Sending the
      // rolled-back version's values instead would be rejected with a 400 if they
      // ever differed. Normalized here for the same reason: the backend's
      // LocaleIdentifier validation rejects the legacy underscore form.
      sourceLocale: normalizeLocaleIdentifier(sourceLocale),
      cxFamilyId,
      // A rollback is submitted as a NEW version on top of the current one, so it must carry the
      // current version as baseVersion — exactly like a routine submit (useSubmitTranslation).
      // The backend derives nextVersion = baseVersion + 1 itself and uses this value only for
      // optimistic-concurrency detection. Omitting it is now rejected outright with a 409 for a
      // group that already has versions (it used to fall into the brand-new-group path,
      // nextVersion=1, and collide at TRANSCOM as a 500). The rollback delta was also computed
      // against currentVersion, so this is the version it must apply to.
      baseVersion: currentVersion.toString(),
      // Marks this submission as a rollback so the backend can distinguish it from a
      // routine content submit (useSubmitTranslation sends SUBMIT).
      submissionType: SubmissionType.Rollback,
      // Recipient for the completion notification. A blank collapses to absent
      // (`|| undefined`) so an empty string never reaches the resolver — the
      // same shape `useSubmitTranslation` sends on the routine-submit path.
      notificationEmail: notificationEmail || undefined,
      // Change-control override. Absent from the variables entirely when nothing
      // was cited — the same `toMcmIdVariable` the submit path uses, so a blank
      // never reaches the resolver as an empty string (the backend answers 400).
      mcmId: toMcmIdVariable(mcmId),
      ...(delta.added.length > 0 ? { added: delta.added } : {}),
      ...(delta.edited.length > 0 ? { edited: delta.edited } : {}),
      ...(delta.deleted.length > 0 ? { deleted: delta.deleted } : {}),
    };

    const { data: mutationData } = await submitMutation({ variables });

    const returnedVersion =
      mutationData?.submitStringGroupTranslation.version || version;

    setNewVersion(returnedVersion);
    setSubmitting(false);
    setShowSuccess(true);
  } catch (err) {
    setSubmitting(false);
    const accessDenied = isAccessDenied(err);
    // A change-control refusal is read through `extractSubmitError`, the same
    // path the Submit modal uses: it takes the MATCHED GraphQL error's message
    // (not the ApolloError aggregate, whose `message` joins every error in the
    // response) and falls back to a change-control-specific sentence when the
    // service left the message blank — so a refusal never reads as a generic
    // rollback failure inside a "refused by change control" dialog.
    const isRefusal = toChangeControlRefusal(err, '') !== undefined;
    const message = accessDenied
      ? getAccessDeniedMessage(err)
      : isRefusal
        ? extractSubmitError(t, err)
        : err instanceof Error
          ? err.message
          : '';
    const shownMessage = message || t(ROLLBACK_FAILED_FALLBACK_KEY);
    setError({
      message: shownMessage,
      accessDenied,
      // Read from the same matched error the message came from, so the link and
      // the sentence above it always describe one refusal.
      deniedNamespace: accessDenied ? getAccessDeniedNamespace(err) : undefined,
      // Carries the SAME sentence as `message`, so the refusal dialog and the
      // generic alert can never show two different accounts of one failure.
      changeControlBlocked: isRefusal
        ? toChangeControlRefusal(err, shownMessage)
        : undefined,
    });
  }
}
